/**
 * 클라이언트와 서버가 공유하는 상수.
 *
 * 여기 있는 값은 서버가 강제하고 클라이언트가 미리 반영한다(입력창 글자 수 제한 등).
 * 한쪽에만 두면 반드시 어긋나므로 단일 진실 원천으로 둔다.
 */

/** 와이어 프로토콜 버전. 호환되지 않는 변경 시 올린다. */
export const PROTOCOL_VERSION = 1;

// ─── 내용 제한 ───────────────────────────────────────────────

export const MAX_BODY_LENGTH = 2_000;
export const MAX_NICKNAME_LENGTH = 20;
export const MAX_ROOM_NAME_LENGTH = 40;

/** 메시지 수정 허용 시간(ms). 이 시간이 지나면 서버가 거부한다. */
export const EDIT_WINDOW_MS = 5 * 60 * 1000;

// ─── 오피스 맵 ───────────────────────────────────────────────

export const TILE_SIZE = 32;
export const MAP_TILES_X = 40;
export const MAP_TILES_Y = 24;

/** 근접 속닥(whisper)이 들리는 반경. 타일 단위. */
export const WHISPER_RADIUS_TILES = 3;

// ─── 타이밍 ─────────────────────────────────────────────────

/** 클라이언트가 move를 보내는 최소 간격(ms). */
export const MOVE_THROTTLE_MS = 100;

/** 서버가 프레즌스 업데이트를 모아 브로드캐스트하는 주기(ms). */
export const PRESENCE_TICK_MS = 50;

/** 클라이언트 ping 주기(ms). */
export const HEARTBEAT_INTERVAL_MS = 25_000;

/** 이 시간 동안 클라이언트 메시지가 없으면 서버가 연결을 끊는다(ms). */
export const CONNECTION_TIMEOUT_MS = 60_000;

/**
 * 이 시간 동안 서버에서 아무 프레임도 오지 않으면 죽은 연결로 본다(ms).
 *
 * TCP 연결이 half-open으로 남으면 close 이벤트가 영영 오지 않는다.
 * 노트북 덮개를 닫았다 열거나 와이파이가 바뀔 때 실제로 일어난다.
 * 그대로 두면 사용자는 끊긴 줄 모르고 혼잣말을 계속하게 된다.
 *
 * ping을 HEARTBEAT_INTERVAL_MS마다 보내고 pong을 받으므로,
 * 두 번 연속 놓치면 끊긴 것으로 판단한다.
 */
export const LIVENESS_TIMEOUT_MS = HEARTBEAT_INTERVAL_MS * 2.5;

/** 아바타 좌표를 SQLite에 저장하는 주기(ms). 매 이동마다 쓰지 않는다. */
export const POSITION_PERSIST_INTERVAL_MS = 30_000;

/** 말풍선이 화면에 떠 있는 시간(ms). */
export const SPEECH_BUBBLE_MS = 4_000;

// ─── 재연결 백오프 ────────────────────────────────────────────

export const RECONNECT_BASE_DELAY_MS = 1_000;
export const RECONNECT_MAX_DELAY_MS = 30_000;
/** 백오프에 곱해지는 지터 비율(0~1). 동시 재연결 쇄도를 막는다. */
export const RECONNECT_JITTER = 0.3;

// ─── 레이트 리밋 (토큰 버킷) ──────────────────────────────────

export interface RateLimitSpec {
  /** 버킷 최대 용량 = 순간 최대 허용 횟수. */
  readonly capacity: number;
  /** 초당 보충되는 토큰 수. */
  readonly refillPerSecond: number;
}

export const RATE_LIMITS = {
  /** 메시지 5개 / 5초 */
  message: { capacity: 5, refillPerSecond: 1 },
  /** 이동 20회 / 초 */
  move: { capacity: 20, refillPerSecond: 20 },
  /** 리액션 20개 / 10초 */
  reaction: { capacity: 20, refillPerSecond: 2 },
  /**
   * 타이핑 표시 10회 / 5초.
   *
   * 클라이언트가 디바운스하지만 그건 클라이언트의 선의에 기대는 것이다.
   * 켜고 끄기를 반복해 남을 괴롭히는 경로를 서버에서 막는다.
   */
  typing: { capacity: 10, refillPerSecond: 2 },
  /** 입장 시도 5회 / 분 */
  join: { capacity: 5, refillPerSecond: 5 / 60 },
} as const satisfies Record<string, RateLimitSpec>;

export type RateLimitKind = keyof typeof RATE_LIMITS;

// ─── 리액션 ─────────────────────────────────────────────────

/**
 * 쓸 수 있는 리액션 이모지.
 *
 * 목록으로 제한한다. 임의 문자열을 허용하면 남의 화면에 아무 텍스트나
 * 띄울 수 있고, 폭이 제각각이라 레이아웃도 무너진다.
 */
export const REACTION_EMOJIS = ['👍', '😂', '😮', '🙏', '🔥', '👀'] as const;
export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

/** 한 메시지에 한 사람이 달 수 있는 리액션 수. */
export const MAX_REACTIONS_PER_USER_PER_MESSAGE = 3;

// ─── 보존 정책 ───────────────────────────────────────────────

/** 방당 보관하는 최대 메시지 수. 초과분은 alarm()이 정리한다. */
export const MESSAGE_RETENTION_COUNT = 5_000;
/**
 * 메시지 보관 기간(일).
 *
 * 3일이다. 처음엔 30일이었는데, 이 앱은 종단간 암호화를 하지 않아서
 * 서버를 띄운 사람이 저장된 대화를 읽을 수 있다(§6.5). 그걸 막는 가장
 * 확실한 방법은 암호화가 아니라 **없애는 것**이다 — 없는 데이터는 아무도
 * 못 읽고, 유출될 수도, 소환당할 수도 없다.
 *
 * 사무실 뒷담화의 유효기간이 사흘을 넘는 일도 드물다. 어제 회의 얘기를
 * 다음 달에 다시 찾아보지 않는다.
 *
 * 이 값을 늘리려면 §6.5의 고지 문구도 함께 고쳐야 한다. 화면에서 "사흘 뒤
 * 지워진다"고 말해 두고 실제로는 더 오래 갖고 있으면 그건 거짓말이 된다.
 *
 * 속닥은 애초에 저장하지 않으므로 이 값과 무관하다(room-do.ts의 handleSend).
 */
export const MESSAGE_RETENTION_DAYS = 3;
/**
 * 삭제 표시된 메시지를 물리 삭제하기까지의 유예(일).
 *
 * 보존 기간(3일)보다 길어서 이 값에 먼저 걸리는 일은 이제 없다. alarm()이
 * created_at 기준으로 먼저 지우기 때문에, 묘비는 아무리 길어도 원 메시지가
 * 태어난 지 3일까지만 산다.
 *
 * 그래도 줄이지 않는다. 묘비는 오프라인이던 클라이언트가 돌아와 "이 메시지는
 * 지워졌다"를 배우라고 있는 것이라, 짧을수록 삭제를 놓친 채 계속 보여 주는
 * 사람이 생긴다. 보존 기간이 상한 노릇을 하는 지금이 원하는 동작이다.
 */
export const SOFT_DELETE_GRACE_DAYS = 7;
